import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ClusterListItem } from '@b4b/shared';
import { useSetRead } from '../api/queries.js';
import CardMenu from './CardMenu.js';
import { useRelativeTime } from '../lib/time.js';
import { displayTitle } from '../lib/cardText.js';
import Placeholder from './Placeholder.js';
import TranslatedBadge from './TranslatedBadge.js';

// Equal-height grid card (Feedly "cards" style, deliberately not masonry):
// 4:3 image, 3-line title, source · time. Tap opens the detail.
export default function GridCard({ item, focused = false }: { item: ClusterListItem; focused?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const relTime = useRelativeTime();
  const setRead = useSetRead();
  const [imgFailed, setImgFailed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const rep = item.representative;
  const extraSources = item.sources.length - 1;

  const openOriginal = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(rep.url, '_blank', 'noopener');
    if (!item.read) setRead.mutate({ id: item.id, read: true });
  };

  return (
    <article
      data-cluster-id={item.id}
      onClick={() => navigate(`/c/${item.id}`)}
      className={`group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition hover:shadow-md dark:bg-neutral-900 ${
        focused ? 'border-accent ring-2 ring-accent/40' : 'border-neutral-200 dark:border-neutral-800'
      } ${item.read ? 'opacity-60' : ''}`}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-neutral-100 dark:bg-neutral-800">
        {rep.imageUrl && !imgFailed ? (
          <img
            src={rep.imageUrl}
            alt=""
            referrerPolicy="no-referrer"
            loading="lazy"
            onError={() => setImgFailed(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <Placeholder title={rep.title} faviconUrl={rep.faviconUrl} className="h-full w-full" />
        )}
        {item.saved && (
          <span aria-hidden className="absolute left-2 top-2 rounded-full bg-amber-500/90 px-1.5 py-0.5 text-xs text-white">
            ★
          </span>
        )}
        {/* same pill style as the reels rail so the overlay reads on light photos too */}
        <button
          onClick={openOriginal}
          aria-label={t('feed.openOriginal')}
          className="absolute right-1.5 top-1.5 flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition hover:bg-black/60 focus-visible:ring-2 focus-visible:ring-white"
        >
          ↗
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
          aria-label={t('feed.actions')}
          aria-expanded={menuOpen}
          className="absolute bottom-1.5 right-1.5 flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition hover:bg-black/60 focus-visible:ring-2 focus-visible:ring-white"
        >
          ⋯
        </button>
        {rep.imageGenerated && rep.imageUrl && !imgFailed && (
          <span className="absolute bottom-1.5 left-2 rounded-full bg-black/45 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-white/80">
            ✦ {t('feed.aiImage')}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-3">
        <h2 className="line-clamp-3 text-sm font-semibold leading-snug">{displayTitle(rep)}</h2>
        <p className="mt-auto flex items-center gap-1 pt-2 text-xs text-neutral-500">
          {rep.faviconUrl && (
            <img
              src={rep.faviconUrl}
              alt=""
              referrerPolicy="no-referrer"
              className="h-3.5 w-3.5 rounded"
              onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
            />
          )}
          <span className="truncate">{rep.feedTitle ?? ''}</span>
          <span>·</span>
          <span className="shrink-0">{relTime(rep.publishedAt)}</span>
          {rep.translation && <TranslatedBadge translation={rep.translation} className="shrink-0" />}
          {extraSources > 0 && (
            <span
              title={t('feed.sourcesBadge', { count: extraSources })}
              aria-label={t('feed.sourcesBadge', { count: extraSources })}
              className="ml-auto shrink-0 rounded-full bg-accent/10 px-1.5 py-0.5 font-medium text-accent"
            >
              +{extraSources}
            </span>
          )}
        </p>
      </div>
      {menuOpen && <CardMenu item={item} onClose={() => setMenuOpen(false)} className="right-2 top-14" />}
    </article>
  );
}
